/**
 * Staff decision cards (#6094): a session writes a card with a question and
 * its options, the course lead taps an answer on the site, and the session
 * reads the answer back. Cards live at `course/{courseId}/decisions/{id}`,
 * are created and closed by admin scripts, and only the answer fields are
 * written from the page (see the Firestore rules for `decisions`).
 */
import {z} from 'zod';
import {FirestoreTimestampSchema} from '../../types';

/** A link shown with a card's context. */
export const DecisionLinkSchema = z.object({
  label: z.string().min(1),
  url: z.url(),
});

/** Where a card stands: open for an answer, decided, or closed without one. */
export const DecisionStatusSchema = z.enum(['open', 'decided', 'closed']);

/** The fields the page writes when the lead taps an option. */
export const DECISION_ANSWER_FIELDS = [
  'choice',
  'note',
  'decidedAt',
  'decidedBy',
  'status',
] as const;

/** One decision card as stored. */
export const DecisionDocumentSchema = z
  .object({
    title: z.string().min(1),
    context: z.string(),
    links: z.array(DecisionLinkSchema),
    options: z.array(z.string().min(1)).min(2).max(6),
    deadline: FirestoreTimestampSchema,
    status: DecisionStatusSchema,
    askedBy: z.string().min(1),
    createdAt: FirestoreTimestampSchema,
    choice: z.string().optional(),
    note: z.string().optional(),
    decidedAt: FirestoreTimestampSchema.optional(),
    decidedBy: z.string().optional(),
    closedReason: z.string().optional(),
  })
  .strict();

/** One decision card as stored. */
export type DecisionDocument = z.infer<typeof DecisionDocumentSchema>;

/**
 * The Firestore path of a course's decisions collection.
 *
 * @param courseId - The course.
 * @return The collection path.
 * @throws Never.
 */
export function getDecisionsCollectionPath(courseId: string): string {
  return `course/${courseId}/decisions`;
}

/**
 * The Firestore path of one decision card.
 *
 * @param params.courseId - The course.
 * @param params.decisionId - The card id.
 * @return The document path.
 * @throws Never.
 */
export function getDecisionDocumentPath({
  courseId,
  decisionId,
}: {
  courseId: string;
  decisionId: string;
}): string {
  return `${getDecisionsCollectionPath(courseId)}/${decisionId}`;
}

/**
 * Builds a readable card id from the date and the title: `2026-10-23-ship-the-catch-up-page`.
 *
 * @param params.title - The card's title.
 * @param params.now - The creation time.
 * @return A slug of the date and up to six title words, lower-case ASCII.
 * @throws Never.
 */
export function buildDecisionId({
  title,
  now,
}: {
  title: string;
  now: Date;
}): string {
  const words = title
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(word => word !== '')
    .slice(0, 6)
    .join('-');
  const date = now.toISOString().slice(0, 10);
  return words === '' ? date : `${date}-${words}`;
}
